import {
  ConsentOrdersFinancialResourcesSchema,
  FinancialResourcesIcon,
  InitialDetails,
  RadioSelect,
  TextArea
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: InitialDetails;
  onSubmit: (values: InitialDetails) => void;
}

export default function FinancialResources({ onSubmit, formId, store }: IProps) {
  const { handleSubmit, watch, control } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(ConsentOrdersFinancialResourcesSchema)
  });

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b">
        <FinancialResourcesIcon classes="w-14 h-14 mx-auto mb-5" />

        <h3 className="text-center">Financial Resources</h3>
      </div>

      <p className="text-gray-500">
        Financial resources are not assets you currently own but are money entitled to in the near future.
      </p>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Do either of you have any significant accrued employment entitlements"
          subTitle="E.g. long service leave, unused annual leave over 8 weeks, redundancy which has initiated but not paid        "
          control={control}
          name="financial_resources.employment_entitlements"
          id="financial_resources.employment_entitlements"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("financial_resources.employment_entitlements") === "yes" ? (
          <TextArea
            title="Give Details"
            subTitle="Please ensure you provide an estimated $ amount."
            control={control}
            name="financial_resources.employment_entitlements_details"
            id="financial_resources.employment_entitlements_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Do either of you expect to receive a significant payment in the future (which has been approved or earned)"
          subTitle="E.g. A tax refund, personal injury payment, settlement from a court case, bonuses which have been earned but not paid,  stock options that haven’t vested"
          control={control}
          name="financial_resources.payout_future"
          id="financial_resources.payout_future"
          options={["yes", "no"]}
          theme="dark"
        />

        {watch("financial_resources.payout_future") === "yes" ? (
          <TextArea
            title="Give Details"
            subTitle="Please ensure you provide an estimated $ amount."
            control={control}
            name="financial_resources.payout_future_details"
            id="financial_resources.payout_future_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      {/*  */}
      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Are either of you entitled to receive an unpaid inheritance from a deceased estate?"
          subTitle="Do not include payments where the death has not occurred (E.g. an assumed inheritance from a family member who is still alive)"
          control={control}
          name="financial_resources.if_unpaid_inheritance"
          id="financial_resources.if_unpaid_inheritance"
          options={["yes", "no"]}
          theme="dark"
        />

        {watch("financial_resources.if_unpaid_inheritance") === "yes" ? (
          <TextArea
            title={<p className="font-bold mt-4 text-sm">Give Details </p>}
            control={control}
            name="financial_resources.unpaid_inheritance_details"
            id="financial_resources.unpaid_inheritance_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Are either of you a beneficiary, have control over, or access to a trust?"
          subTitle="E.g. A family trust or unit trust"
          control={control}
          name="financial_resources.beneficiary_trust"
          id="financial_resources.beneficiary_trust"
          options={["yes", "no"]}
          theme="dark"
        />

        {watch("financial_resources.beneficiary_trust") === "yes" ? (
          <TextArea
            title="Give Details"
            control={control}
            name="financial_resources.beneficiary_trust_details"
            id="financial_resources.beneficiary_trust_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>

      <div className="bg-[#f2f2f2] p-2 md:p-5 rounded-lg space-y-3">
        <RadioSelect
          title="Do either of you have any life insurance that can cashed-out before death?"
          subTitle="Do not include any life insurance policies (including those with super) which only payout upon death, and that cannot be cached out while you’re alive."
          control={control}
          name="financial_resources.cash_life_insurance"
          id="financial_resources.cash_life_insurance"
          options={["yes", "no"]}
          theme="dark"
        />
        {watch("financial_resources.cash_life_insurance") === "yes" ? (
          <TextArea
            title="Give Details"
            control={control}
            name="financial_resources.cash_life_insurance_details"
            id="financial_resources.cash_life_insurance_details"
            placeholder="details"
            theme="dark"
          />
        ) : null}
      </div>
    </form>
  );
}
